import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';
import type {
  DataMappingResponse,
  DataMappingSyncResponse,
  ResolvedDataMappingResponse,
} from '@erp/rpc';

/** Exported so callers that change a mapping's inputs can invalidate it too. */
export const DATA_MAPPINGS_KEY = 'data-mappings';

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: [DATA_MAPPINGS_KEY] });
}

export function useListDataMappings() {
  return useQuery<{ dataMappings: DataMappingResponse[] }>({
    queryKey: [DATA_MAPPINGS_KEY, 'list'],
    queryFn: () => rpcClient.dataMappings.list(),
  });
}

export function useGetDataMapping(id: string | undefined) {
  return useQuery<ResolvedDataMappingResponse>({
    queryKey: [DATA_MAPPINGS_KEY, 'detail', id],
    queryFn: () => {
      if (!id) throw new Error('useGetDataMapping: id is required');
      return rpcClient.dataMappings.get({ id });
    },
    enabled: !!id,
  });
}

export interface DataMappingRowsResult {
  rows: Record<string, Record<string, unknown>>;
  /** Rows the table holds, whatever `limit` returned. */
  total: number;
  hasMore: boolean;
}

/**
 * A window onto the mapping's rows. `limit` is part of the query key, so
 * widening it fetches once and then serves from cache on the way back.
 */
export function useDataMappingRows(id: string | undefined, limit: number) {
  return useQuery<DataMappingRowsResult>({
    queryKey: [DATA_MAPPINGS_KEY, 'rows', id, limit],
    queryFn: () => {
      if (!id) throw new Error('useDataMappingRows: id is required');
      return rpcClient.dataMappings.readRows({ id, limit });
    },
    enabled: !!id,
    // Keeps the previous window on screen while a wider one loads, so pressing
    // "show more" grows the table instead of blanking it.
    placeholderData: (previous) => previous,
  });
}

export function useCreateDataMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Parameters<typeof rpcClient.dataMappings.create>[0]) =>
      rpcClient.dataMappings.create(input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useUpdateDataMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Parameters<typeof rpcClient.dataMappings.update>[0]) =>
      rpcClient.dataMappings.update(input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useDeleteDataMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => rpcClient.dataMappings.delete({ id }),
    onSuccess: () => invalidate(queryClient),
  });
}

/** Push a new shape to the mapping's stateful table (recasts existing rows). */
export function useUpdateDataMappingSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      input: Parameters<typeof rpcClient.dataMappings.updateSchema>[0],
    ) => rpcClient.dataMappings.updateSchema(input),
    onSuccess: () => invalidate(queryClient),
  });
}

export type UniqueIndexDiscoveryResponse = Awaited<
  ReturnType<typeof rpcClient.dataMappings.discoverUniqueIndexes>
>;

/**
 * Analyze the mapping's rows for unique indexes and store the verified set on
 * its schema definition. Also runs automatically after each source sync.
 */
export function useDiscoverUniqueIndexes() {
  const queryClient = useQueryClient();

  return useMutation<
    UniqueIndexDiscoveryResponse,
    Error,
    Parameters<typeof rpcClient.dataMappings.discoverUniqueIndexes>[0]
  >({
    mutationFn: (input) => rpcClient.dataMappings.discoverUniqueIndexes(input),
    onSuccess: () => invalidate(queryClient),
  });
}

/** Store the confirmed unique-index set (the write half of discovery). */
export function useSetUniqueIndexes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      input: Parameters<typeof rpcClient.dataMappings.setUniqueIndexes>[0],
    ) => rpcClient.dataMappings.setUniqueIndexes(input),
    onSuccess: () => invalidate(queryClient),
  });
}

/** Push the source asset's contents into the stateful table. */
export function useSyncDataMappingFromSource() {
  const queryClient = useQueryClient();

  return useMutation<
    DataMappingSyncResponse,
    Error,
    Parameters<typeof rpcClient.dataMappings.syncFromSource>[0]
  >({
    mutationFn: (input) => rpcClient.dataMappings.syncFromSource(input),
    onSuccess: () => invalidate(queryClient),
  });
}
